import { CartLinesUpdateEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');

const formatMoney = (cents, format) => {
  const value = (Number(cents) / 100).toFixed(2);
  const [whole, decimals] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return format
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`)
    .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimals}`);
};

/**
 * Changes the cart and tells Horizon about it, so the drawer, cart count and totals re-render.
 * @param {HTMLElement} source
 * @param {'add' | 'remove'} action
 * @param {{ variantId?: string, lineKey?: string }} line
 */
async function updateCart(source, action, { variantId, lineKey }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: action === 'add' ? 'product' : 'cart',
      lines: [action === 'add' ? { merchandiseId: variantId, quantity: 1 } : { id: lineKey, quantity: 0 }],
      promise: deferred.promise,
    })
  );

  try {
    const sections = cartSectionIds();
    const request =
      action === 'add'
        ? fetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections }),
          })
        : fetch(`${root()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: 0, sections }),
          });
    const result = await (await request).json();
    const cart = action === 'add' ? await (await fetch(`${root()}cart.js`)).json() : result;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: source.localName,
        sections: result.sections,
        didError: Boolean(result.status),
      },
    });
    return !result.status;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/* ---------- Scrolling upsell rows ---------- */
if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-id', 'data-exclude', 'hidden'];
    }

    connectedCallback() {
      // The drawer re-renders by morphing server HTML, which empties the list; put the products back.
      this.observer = new MutationObserver(() => this.restore());
      const track = this.querySelector('.cart-upsell__track');
      if (track) this.observer.observe(track, { childList: true });
      this.querySelectorAll('.cart-upsell__arrow').forEach((arrow) =>
        arrow.addEventListener('click', () => {
          const card = track?.querySelector('.cart-upsell__card');
          const step = card ? card.getBoundingClientRect().width + 10 : 150;
          track?.scrollBy({ left: step * Number(arrow.dataset.dir), behavior: 'smooth' });
        })
      );
      this.load();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'hidden') {
        if (newValue !== null) this.restore();
        return;
      }
      if (oldValue !== null && oldValue !== newValue) this.load();
    }

    restore() {
      const track = this.querySelector('.cart-upsell__track');
      if (!this.products?.length || !track) return;
      if (track.children.length === 0 || this.hasAttribute('hidden')) this.render(this.products);
    }

    async load() {
      const { source = 'related', productId, limit = '6' } = this.dataset;
      const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean).map(Number));
      let products = [];

      try {
        const embedded = this.querySelector('[data-upsell-products]');
        if (embedded) {
          products = JSON.parse(embedded.textContent || '[]');
        } else if (productId) {
          const intent = source === 'complementary' ? 'complementary' : 'related';
          const response = await fetch(`${root()}recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`);
          products = (await response.json()).products || [];
        }
      } catch {
        products = [];
      }

      const picks = products
        .filter((p) => !exclude.has(p.id) && p.available !== false && p.variants?.some((v) => v.available !== false))
        .slice(0, Number(limit));
      this.render(picks);
    }

    render(products) {
      const track = this.querySelector('.cart-upsell__track');
      if (!track || products.length === 0) {
        this.products = [];
        this.hidden = true;
        return;
      }
      this.products = products;
      const format = this.dataset.moneyFormat || '{{amount}}';
      track.innerHTML = '';

      products.forEach((product) => {
        const variant = product.variants.find((v) => v.available !== false) || product.variants[0];
        const onSale = variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);
        let image = product.featured_image || '';
        if (image && !image.includes('width=')) image += `${image.includes('?') ? '&' : '?'}width=400`;

        const li = document.createElement('li');
        li.className = 'cart-upsell__card';
        li.innerHTML = `
          <a class="cart-upsell__image" href="${product.url}">${image ? `<img src="${image}" alt="" loading="lazy">` : ''}</a>
          <a class="cart-upsell__name" href="${product.url}"></a>
          <div class="cart-upsell__price">
            ${onSale ? `<s>${formatMoney(variant.compare_at_price, format)}</s>` : ''}
            <span class="${onSale ? 'is-sale' : ''}">${formatMoney(variant.price, format)}</span>
          </div>
          <button type="button" class="cart-upsell__add button"></button>`;
        li.querySelector('.cart-upsell__name').textContent = product.title;
        const button = li.querySelector('.cart-upsell__add');
        button.textContent = this.dataset.buttonLabel || 'Add';
        button.addEventListener('click', async () => {
          if (button.disabled) return;
          button.disabled = true;
          button.classList.add('is-loading');
          const ok = await updateCart(this, 'add', { variantId: String(variant.id) });
          if (!ok) {
            button.disabled = false;
            button.classList.remove('is-loading');
          }
        });
        track.append(li);
      });
      this.hidden = false;
    }
  }

  customElements.define('cart-upsell', CartUpsell);
}

/* ---------- Shipping protection toggle ---------- */
if (!customElements.get('shipping-protection')) {
  const OPT_OUT_KEY = 'shipping-protection-off';
  const optedOut = () => {
    try {
      return localStorage.getItem(OPT_OUT_KEY) === '1';
    } catch {
      return false;
    }
  };
  const setOptedOut = (value) => {
    try {
      value ? localStorage.setItem(OPT_OUT_KEY, '1') : localStorage.removeItem(OPT_OUT_KEY);
    } catch {}
  };

  class ShippingProtection extends HTMLElement {
    static get observedAttributes() {
      return ['data-in-cart', 'data-has-items', 'data-line-key'];
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-protection-toggle]')) this.onToggle(event.target.checked);
      });
      this.sync();
    }

    attributeChangedCallback() {
      if (this.isConnected) this.sync();
    }

    get inCart() {
      return this.dataset.inCart === 'true';
    }

    // Keep the cart in line with the shopper's choice: add by default, remove when the cart has nothing else.
    async sync() {
      if (this.busy) return;
      const hasItems = this.dataset.hasItems === 'true';
      if (!hasItems && this.inCart) return this.run('remove');
      if (hasItems && !this.inCart && this.dataset.defaultOn === 'true' && !optedOut() && !this.autoAdded) {
        this.autoAdded = true;
        return this.run('add');
      }
    }

    async onToggle(checked) {
      setOptedOut(!checked);
      if (checked && !this.inCart) await this.run('add');
      if (!checked && this.inCart) await this.run('remove');
    }

    async run(action) {
      this.busy = true;
      this.classList.add('is-loading');
      await updateCart(this, action, { variantId: this.dataset.variantId, lineKey: this.dataset.lineKey });
      this.busy = false;
      this.classList.remove('is-loading');
    }
  }

  customElements.define('shipping-protection', ShippingProtection);
}
